<template>
  <v-dialog :value="true" persistent max-width="900">
    <v-card style="display: flex; flex-direction: column; max-height: 90vh">
      <!-- Titulo siempre fijo -->
      <v-card-title>
        <v-row justify="center" class="mb-5">
          <div>
            {{
              !isMobile
                ? $t('searches.advancedSearch')
                : $t('searches.advancedSearchShort')
            }}
          </div>
        </v-row>
      </v-card-title>
      <!-- Cuerpo scrollable -->
      <v-card-text style="overflow-y: auto; flex: 1; position: relative">
        <v-form
          ref="advancedSearchForm"
          v-model="validAdvancedSearchForm"
          @submit.prevent="() => {}"
          lazy-validation
        >
          <v-row>
            <!-- selector múltiple para Empresas, con búsqueda -->
            <v-col cols="12" no-gutters align="baseline">
              <div id="cifs-wrapper" style="position: relative">
                <v-autocomplete
                  prepend-icon="mdi-office-building"
                  v-model="selectedCifs"
                  :items="cifsWithDisplay"
                  :search-input.sync="searchCif"
                  clearable
                  chips
                  multiple
                  small-chips
                  deletable-chips
                  color="primary"
                  hide-selected
                  :label="cifLabel"
                  item-text="empresa"
                  item-value="numeroDocIdentidad"
                  :filter="customFilterCifs"
                  :no-data-text="
                    visibleItemsCifs?.length ? '' : $t('searches.noData')
                  "
                  :disabled="loading"
                  :close-on-select="false"
                  :menu-props="{
                    closeOnContentClick: false,
                    openOnFocus: false,
                    maxHeight: '300',
                    bottom: true,
                    offsetY: true,
                    attach: '#cifs-wrapper',
                  }"
                  append-icon="mdi-menu-down"
                  ref="cifsAutocomplete"
                  id="cifsAutocomplete"
                >
                  <!-- Slot para el icono y poder cambiar de icono y de color cuando el criterio es utilizado -->
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(selectedCifs)">
                      {{
                        selectedCifs?.length
                          ? 'mdi-magnify-plus'
                          : 'mdi-office-building'
                      }}
                    </v-icon>
                  </template>

                  <!-- Slot para el menú: añadimos el botón 'Seleccionar todo' -->
                  <template v-slot:prepend-item>
                    <v-list-item
                      v-if="visibleItemsCifs?.length"
                      @click="selectAllFilteredCifs"
                    >
                      <v-list-item-action>
                        <v-icon>mdi-check-all</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title
                          >{{ $t('searches.selectAll') }} ({{
                            visibleItemsCifs?.length
                          }})</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <!-- Slot para cada item: interceptamos el click -->
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      @click="onItemCifsClick"
                    >
                      <v-list-item-title>{{ item.display }}</v-list-item-title>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>

            <!-- Empresas no registradas: solo visible para clientes de tipo Empresa -->
            <v-col
              cols="12"
              class="pl-4"
              v-if="isEmpresaUser && isAllowedRecogerNoRegistradas"
            >
              <v-row dense align="center" class="mb-0">
                <v-icon
                  class="mr-2"
                  :color="
                    iconColor(cifsNotRegisteredShowAll || cifsNotRegistered)
                  "
                >
                  {{
                    cifsNotRegisteredShowAll == true || cifsNotRegistered
                      ? 'mdi-magnify-plus'
                      : 'mdi-account-lock-open'
                  }}
                </v-icon>
                <span class="subtitle-1">{{
                  $t('searches.cifsNotRegistered')
                }}</span>
              </v-row>
              <v-row dense align="center" class="ml-6 mt-n2">
                <!-- checkbox todas -->
                <v-col cols="12" md="4" class="py-0">
                  <v-checkbox
                    class="mr-3"
                    :disabled="loading"
                    v-model="cifsNotRegisteredShowAll"
                    :label="$t('searches.cifsNotRegisteredShowAll')"
                    id="cifsNotRegisteredShowAll"
                  />
                </v-col>
                <!-- empresas no registradas, nifs o nombres separados por coma -->
                <v-col
                  cols="12"
                  md="8"
                  class="py-0"
                  :class="{ 'mt-n4': isMobile }"
                >
                  <v-text-field
                    v-model="cifsNotRegistered"
                    @focus="isCifsNotRegisteredFocused = true"
                    @blur="isCifsNotRegisteredFocused = false"
                    :label="cifsNotRegisteredLabel"
                    prepend-icon="mdi-file-document-edit"
                    :disabled="loading"
                    autocomplete="false"
                    :rules="[]"
                    id="cifsNotRegistered"
                  >
                    <template v-slot:prepend>
                      <v-icon :color="iconColor(cifsNotRegistered)">
                        {{
                          cifsNotRegistered
                            ? 'mdi-magnify-plus'
                            : 'mdi-file-document-edit'
                        }}
                      </v-icon>
                    </template>
                  </v-text-field>
                </v-col>
              </v-row>
            </v-col>

            <!-- checkboxes casos especiales, solo para superadmin, los operadores no ven los documentos sin nif titular -->
            <v-col v-if="!isOperatorUser" cols="12" class="pl-4">
              <v-row dense align="center" class="mb-0">
                <v-icon
                  class="mr-2"
                  :color="iconColor(!withoutNifTitular || !ignoreNifTitular)"
                >
                  {{
                    withoutNifTitular == false || ignoreNifTitular == false
                      ? 'mdi-magnify-plus'
                      : 'mdi-account-lock-open'
                  }}
                </v-icon>
                <span class="subtitle-1">{{
                  $t('searches.specialCases')
                }}</span>
              </v-row>
              <v-row dense align="center" class="ml-6 mt-n2">
                <v-col cols="12" md="4" class="py-0">
                  <v-checkbox
                    class="mr-3"
                    :disabled="loading"
                    v-model="withoutNifTitular"
                    :label="$t('searches.withoutNifTitular')"
                    id="withoutNifTitular"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="5"
                  class="py-0"
                  :class="{ 'mt-n8': isMobile }"
                >
                  <v-checkbox
                    :disabled="loading"
                    v-model="ignoreNifTitular"
                    :label="$t('searches.ignoreNifTitular')"
                    id="ignoreNifTitular"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- selector multiple para estados, con busqueda -->
            <v-col cols="12">
              <div id="status-wrapper" style="position: relative">
                <v-autocomplete
                  prepend-icon="mdi-state-machine"
                  v-model="selectedStatus"
                  :items="status"
                  clearable
                  chips
                  multiple
                  small-chips
                  deletable-chips
                  color="primary"
                  :label="$t('searches.selectStatus')"
                  item-text="estado"
                  item-value="estado"
                  :filter="customStatusFilter"
                  :no-data-text="status?.length ? '' : $t('searches.noData')"
                  :disabled="loading"
                  :menu-props="{
                    closeOnContentClick: false,
                    openOnFocus: false,
                    bottom: true,
                    offsetY: true,
                    attach: '#status-wrapper',
                  }"
                  append-icon="mdi-menu-down"
                  ref="statusAutocomplete"
                  id="statusAutocomplete"
                >
                  <!-- Slot para el icono y poder cambiar de icono y de color cuando el criterio es utilizado -->
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(selectedStatus)">
                      {{
                        selectedStatus?.length
                          ? 'mdi-magnify-plus'
                          : 'mdi-state-machine'
                      }}
                    </v-icon>
                  </template>

                  <!-- Slot de Seleccionar todo  -->
                  <template v-slot:prepend-item>
                    <v-list-item
                      v-if="status?.length"
                      ripple
                      @mousedown.prevent
                      @click="selectAllStatus"
                    >
                      <v-list-item-icon class="my-3 mr-2">
                        <v-icon
                          :color="
                            selectedStatus?.length > 0 ? 'indigo darken-4' : ''
                          "
                        >
                          {{ iconSelectAllStatus }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title id="status-select-all">
                          {{ $t('searches.selectAll') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider light class="mt-0"></v-divider>
                  </template>

                  <!-- Chips de selección -->
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="handleStatusCheckboxClick(data.item)"
                      :disabled="loading"
                      :color="data.item.color"
                      class="ma-2 white--text"
                    >
                      {{ $t(data.item.status) }}
                    </v-chip>
                  </template>

                  <!-- Listado de items con checkbox -->
                  <template v-slot:item="{ item }">
                    <template>
                      <v-list-item-icon class="my-3 mr-0">
                        <v-simple-checkbox
                          :value="selectedStatus.includes(item.estado)"
                          :ripple="false"
                          @click="handleStatusCheckboxClick(item)"
                        >
                        </v-simple-checkbox>
                      </v-list-item-icon>
                      <v-list-item-content
                        :value="selectedStatus.includes(item.estado)"
                      >
                        <v-list-item-title>
                          {{ $t(item.status) }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>

            <!-- selector multiple para filtros generales, con busqueda -->
            <v-col cols="12">
              <v-row
                id="filters-wrapper"
                style="position: relative"
                no-gutters
                align="baseline"
              >
                <v-autocomplete
                  prepend-icon="mdi-filter"
                  v-model="selectedFilters"
                  :items="filtersFiltered"
                  clearable
                  chips
                  multiple
                  small-chips
                  deletable-chips
                  color="primary"
                  :label="$t('searches.selectFilters')"
                  item-text="name"
                  item-value="id"
                  :filter="customRoleFilter"
                  :no-data-text="
                    filtersFiltered?.length ? '' : $t('searches.noData')
                  "
                  :disabled="loading || customFilter"
                  :menu-props="{
                    bottom: true,
                    offsetY: true,
                    attach: '#filters-wrapper',
                  }"
                  id="filtersAutocomplete"
                  ref="filtersAutocomplete"
                >
                  <!-- Slot para el icono y poder cambiar de icono y de color cuando el criterio es utilizado -->
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(selectedFilters)">
                      {{
                        selectedFilters?.length
                          ? 'mdi-magnify-plus'
                          : 'mdi-filter'
                      }}
                    </v-icon>
                  </template>

                  <template v-slot:prepend-item>
                    <v-list-item
                      v-if="filtersFiltered?.length"
                      ripple
                      @mousedown.prevent
                      @click="selectAllFilters"
                    >
                      <v-list-item-icon class="my-3 mr-2">
                        <v-icon
                          :color="
                            selectedFilters?.length > 0 ? 'indigo darken-4' : ''
                          "
                        >
                          {{ iconSelectAllFilters }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title id="filter-select-all">
                          {{ $t('searches.selectAll') }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider light class="mt-0"></v-divider>
                  </template>
                  <template v-slot:selection="data">
                    <v-chip
                      small
                      v-bind="data.attrs"
                      :input-value="data.selected"
                      close
                      @click="data.select"
                      @click:close="handleFilterCheckboxClick(data.item)"
                      :disabled="loading"
                    >
                      {{
                        data.item.isCustom !== false
                          ? `${data.item.name} (${$t('filters.customFilter')})`
                          : $t(`filters.${data.item.name}`)
                      }}
                    </v-chip>
                  </template>
                  <template v-slot:item="{ item }">
                    <template>
                      <v-list-item-icon class="my-3 mr-0">
                        <v-simple-checkbox
                          :value="selectedFilters.includes(item.id)"
                          :ripple="false"
                          @click="handleFilterCheckboxClick(item)"
                        >
                        </v-simple-checkbox>
                      </v-list-item-icon>
                      <v-list-item-content
                        :value="selectedFilters.includes(item.id)"
                      >
                        <v-list-item-title>
                          {{
                            item.isCustom !== false
                              ? `${item.name} (${$t('filters.customFilter')})`
                              : $t(`filters.${item.name}`)
                          }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </template>
                </v-autocomplete>

                <!-- filtros personalizados -->
                <v-checkbox
                  class="my-checkbox ml-3 mt-0"
                  :disabled="loading"
                  v-model="customFilter"
                  :label="$t('filters.customFilter')"
                  id="custom-filter"
                />
              </v-row>
            </v-col>

            <template v-if="customFilter">
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="codigo_organismo_emisor_raiz"
                  @input="codigo_organismo_emisor_raiz = $event"
                  @keyup="fixAndUppercase('codigo_organismo_emisor_raiz')"
                  :label="$t('notificationProperties.orgSenderSourceCode')"
                  prepend-icon="mdi-numeric"
                  :disabled="loading"
                  :rules="[
                    startByCharacters(['E', 'A', 'L', 'U', 'I', 'J']),
                    numCharacters(9),
                  ]"
                  id="codigo_organismo_emisor_raiz"
                >
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(codigo_organismo_emisor_raiz)">
                      {{
                        codigo_organismo_emisor_raiz
                          ? 'mdi-magnify-plus'
                          : 'mdi-numeric'
                      }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="nombre_organismo_emisor_raiz"
                  @input="nombre_organismo_emisor_raiz = $event"
                  :label="$t('notificationProperties.orgSenderSourceName')"
                  prepend-icon="mdi-tree"
                  :disabled="loading"
                  :rules="[]"
                  id="nombre_organismo_emisor_raiz"
                >
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(nombre_organismo_emisor_raiz)">
                      {{
                        nombre_organismo_emisor_raiz
                          ? 'mdi-magnify-plus'
                          : 'mdi-tree'
                      }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="codigo_organismo_emisor"
                  @input="codigo_organismo_emisor = $event"
                  @keyup="fixAndUppercase('codigo_organismo_emisor')"
                  :label="$t('notificationProperties.orgSenderCode')"
                  prepend-icon="mdi-numeric"
                  :disabled="loading"
                  :rules="[
                    startByCharacters(['E', 'A', 'L', 'U', 'I', 'J']),
                    numCharacters(9),
                  ]"
                  id="codigo_organismo_emisor"
                >
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(codigo_organismo_emisor)">
                      {{
                        codigo_organismo_emisor
                          ? 'mdi-magnify-plus'
                          : 'mdi-numeric'
                      }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>

              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="nombre_organismo_emisor"
                  @input="nombre_organismo_emisor = $event"
                  :label="$t('notificationProperties.orgSenderName')"
                  prepend-icon="mdi-source-commit-start-next-local"
                  :disabled="loading"
                  :rules="[]"
                  id="nombre_organismo_emisor"
                >
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(nombre_organismo_emisor)">
                      {{
                        nombre_organismo_emisor
                          ? 'mdi-magnify-plus'
                          : 'mdi-source-commit-start-next-local'
                      }}
                    </v-icon>
                  </template>
                </v-text-field>
              </v-col>
            </template>

            <!-- selector múltiple para usuarios Responsable, con búsqueda -->
            <v-col cols="6" no-gutters align="baseline">
              <div id="users-wrapper" style="position: relative">
                <v-autocomplete
                  prepend-icon="mdi-account-check"
                  v-model="selectedUsers"
                  :items="usersWithDisplay"
                  :item-disabled="() => false"
                  :search-input.sync="searchUser"
                  clearable
                  chips
                  small-chips
                  multiple
                  deletable-chips
                  color="primary"
                  hide-selected
                  :label="userLabel"
                  item-text="display"
                  item-value="id"
                  :filter="customFilterUsers"
                  :no-data-text="
                    visibleItemsUsers?.length ? '' : $t('searches.noData')
                  "
                  :disabled="loading"
                  :close-on-select="false"
                  :menu-props="{
                    closeOnContentClick: false,
                    openOnFocus: false,
                    maxHeight: '300',
                    bottom: true,
                    offsetY: true,
                    attach: '#users-wrapper',
                  }"
                  append-icon="mdi-menu-down"
                  ref="usersAutocomplete"
                  id="usersAutocomplete"
                >
                  <!-- Slot para el icono y poder cambiar de icono y de color cuando el criterio es utilizado -->
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(selectedUsers)">
                      {{
                        selectedUsers?.length
                          ? 'mdi-magnify-plus'
                          : 'mdi-account-check'
                      }}
                    </v-icon>
                  </template>

                  <!-- Slot para el menú: añadimos el botón 'Seleccionar todo' -->
                  <template v-slot:prepend-item>
                    <v-list-item
                      v-if="visibleItemsUsers?.length"
                      @click="selectAllFilteredUsers"
                    >
                      <v-list-item-action>
                        <v-icon>mdi-check-all</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title
                          >{{ $t('searches.selectAll') }} ({{
                            visibleItemsUsers?.length
                          }})</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <!-- Slot para cada item: interceptamos el click -->
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      @click="onItemUsersClick"
                    >
                      <AppAvatar
                        class="mr-3"
                        :userName="item.display"
                        :userAvatar="item?.avatarURL"
                        :isGroup="item.isGroup || false"
                        :avatarSize="item.isGroup ? '34' : '30'"
                        :initialsSize="item.isGroup ? 11 : 14"
                        iconSize="24"
                        avatarColor="primary"
                        initialsColor="secondary"
                      />
                      <v-list-item-title
                        :class="{
                          'text--disabled': item.disabled || item.deleted,
                        }"
                        >{{ item.display }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                  <!-- slot para customizar el chip seleccionado -->
                  <template v-slot:selection="{ item, attrs }">
                    <v-chip
                      v-bind="attrs"
                      close
                      class="ma-1"
                      :class="{
                        'custom-chip-disabled': item.disabled || item.deleted,
                      }"
                      @click.stop
                      @click:close="removeUser(item)"
                    >
                      <AppAvatar
                        class="mr-2"
                        :userName="item.display"
                        :userAvatar="item?.avatarURL"
                        :isGroup="item.isGroup || false"
                        :avatarSize="item.isGroup ? '32' : '30'"
                        :initialsSize="item.isGroup ? 11 : 14"
                        iconSize="24"
                        avatarColor="primary"
                        initialsColor="secondary"
                      />
                      {{ item.display }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>

            <!-- selector múltiple para Grupos Responsable, con búsqueda -->
            <v-col cols="6" no-gutters align="baseline">
              <div id="groups-wrapper" style="position: relative">
                <v-autocomplete
                  prepend-icon="mdi-account-multiple-check"
                  v-model="selectedGroups"
                  :items="groupsWithDisplay"
                  :item-disabled="() => false"
                  :search-input.sync="searchGroup"
                  clearable
                  chips
                  small-chips
                  multiple
                  deletable-chips
                  color="primary"
                  hide-selected
                  :label="groupLabel"
                  item-text="display"
                  item-value="id"
                  :filter="customFilterGroups"
                  :no-data-text="
                    visibleItemsGroups?.length ? '' : $t('searches.noData')
                  "
                  :disabled="loading"
                  :close-on-select="false"
                  :menu-props="{
                    closeOnContentClick: false,
                    openOnFocus: false,
                    maxHeight: '300',
                    bottom: true,
                    offsetY: true,
                    attach: '#groups-wrapper',
                  }"
                  append-icon="mdi-menu-down"
                  ref="groupsAutocomplete"
                  id="groupsAutocomplete"
                >
                  <!-- Slot para el icono y poder cambiar de icono y de color cuando el criterio es utilizado -->
                  <template v-slot:prepend>
                    <v-icon :color="iconColor(selectedGroups)">
                      {{
                        selectedGroups?.length
                          ? 'mdi-magnify-plus'
                          : 'mdi-account-multiple-check'
                      }}
                    </v-icon>
                  </template>

                  <!-- Slot para el menú: añadimos el botón 'Seleccionar todo' -->
                  <template v-slot:prepend-item>
                    <v-list-item
                      v-if="visibleItemsGroups?.length"
                      @click="selectAllFilteredGroups"
                    >
                      <v-list-item-action>
                        <v-icon>mdi-check-all</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title
                          >{{ $t('searches.selectAll') }} ({{
                            visibleItemsGroups?.length
                          }})</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>

                  <!-- Slot para cada item: interceptamos el click -->
                  <template v-slot:item="{ item, attrs, on }">
                    <v-list-item
                      v-bind="attrs"
                      v-on="on"
                      @click="onItemGroupsClick"
                    >
                      <AppAvatar
                        class="mr-3"
                        :userName="item.display"
                        :userAvatar="item?.avatarURL"
                        :isGroup="item.isGroup || false"
                        :isDisabledOrDeleted="item.disabled || item.deleted"
                        :avatarSize="item.isGroup ? '34' : '30'"
                        :initialsSize="item.isGroup ? 11 : 14"
                        iconSize="24"
                        avatarColor="primary"
                        initialsColor="secondary"
                      />
                      <v-list-item-title
                        :class="{
                          'text--disabled': item.disabled || item.deleted,
                        }"
                        >{{ item.display }}
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                  <!-- slot para customizar el chip seleccionado -->
                  <template v-slot:selection="{ item, attrs }">
                    <v-chip
                      v-bind="attrs"
                      close
                      class="ma-1 custom-chip-tall"
                      :class="{
                        'custom-chip-disabled': item.disabled || item.deleted,
                      }"
                      @click.stop
                      @click:close="removeGroup(item)"
                    >
                      <AppAvatar
                        class="mr-2"
                        :userName="item.display"
                        :userAvatar="item?.avatarURL"
                        :isGroup="item.isGroup || false"
                        :isDisabledOrDeleted="item.disabled || item.deleted"
                        :avatarSize="item.isGroup ? '34' : '30'"
                        :initialsSize="item.isGroup ? 11 : 14"
                        iconSize="24"
                        avatarColor="primary"
                        initialsColor="secondary"
                      />
                      {{ item.display }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
            <!-- hasta aqui, selector de grupos -->

            <!-- Checkbox incluir notificaciones Sin responsable -->
            <v-col cols="12" class="pl-4">
              <v-row dense align="center" class="mb-0">
                <!--v-icon class="mr-2">mdi-account-lock-open</v-icon-->
                <v-icon class="mr-2" :color="iconColor(!withoutResponsible)">
                  {{
                    withoutResponsible == false
                      ? 'mdi-magnify-plus'
                      : 'mdi-account-lock-open'
                  }}
                </v-icon>
                <span class="subtitle-1">{{
                  $t('searches.whenNoResponsible')
                }}</span>
              </v-row>
              <v-row dense align="center" class="ml-6 mt-n2">
                <v-col cols="12" md="6" class="py-0">
                  <v-checkbox
                    class="mr-3"
                    :disabled="loading"
                    v-model="withoutResponsible"
                    :label="$t('searches.withoutResponsible')"
                    id="withoutResponsible"
                  />
                </v-col>
              </v-row>
            </v-col>

            <!-- Alertas -->
            <v-col cols="12" no-gutters align="baseline">
              <v-sheet class="pa-4 mb-4" outlined elevation="1">
                <div class="custom-group-header text--secondary mb-0">
                  <v-icon class="mr-2">mdi-bell</v-icon>
                  <span class="subtitle-1">{{ $t('searches.alerts') }}</span>
                </div>
                <v-row
                  dense
                  align="center"
                  justify="space-between"
                  class="mb-0"
                >
                  <!-- Selector múltiple de estados de alerta con búsqueda -->
                  <v-col cols="12" sm="12" md="6" class="mt-n2">
                    <div id="alertstatus-wrapper" style="position: relative">
                      <v-autocomplete
                        :class="{ 'custom-multiline-select': isMobile }"
                        prepend-icon="mdi-state-machine"
                        v-model="selectedAlertStatus"
                        :items="alertsWithDisplay"
                        clearable
                        chips
                        multiple
                        small-chips
                        deletable-chips
                        color="primary"
                        :label="$t('searches.selectStatus')"
                        item-text="display"
                        item-value="id"
                        return-object
                        :filter="customAlertStatusFilter"
                        :no-data-text="
                          alertsWithDisplay?.length ? '' : $t('searches.noData')
                        "
                        :disabled="loading"
                        :menu-props="{
                          closeOnContentClick: false,
                          openOnFocus: false,
                          bottom: true,
                          offsetY: true,
                          attach: '#alertstatus-wrapper',
                        }"
                        append-icon="mdi-menu-down"
                        ref="alertStatusAutocomplete"
                        id="alertStatusAutocomplete"
                      >
                        <!-- Slot para el icono y poder cambiar de icono y de color cuando el criterio es utilizado -->
                        <template v-slot:prepend>
                          <v-icon :color="iconColor(selectedAlertStatus)">
                            {{
                              selectedAlertStatus?.length
                                ? 'mdi-magnify-plus'
                                : 'mdi-state-machine'
                            }}
                          </v-icon>
                        </template>

                        <!-- Slot para el menú: añadimos el botón 'Seleccionar todo' -->
                        <template v-slot:prepend-item>
                          <v-list-item
                            v-if="alertsWithDisplay?.length"
                            ripple
                            @mousedown.prevent
                            @click="selectAllAlertStatus"
                          >
                            <v-list-item-icon class="my-3 mr-2">
                              <v-icon
                                :color="
                                  selectedAlertStatus.length
                                    ? 'indigo darken-4'
                                    : ''
                                "
                              >
                                {{ iconSelectAllAlertStatus }}
                              </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title id="alertstatus-select-all">
                                {{ $t('searches.selectAll') }}
                              </v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                          <v-divider light class="mt-0"></v-divider>
                        </template>

                        <!-- Chips de selección -->
                        <template
                          v-slot:selection="{ item, attrs, select, selected }"
                        >
                          <v-chip
                            small
                            v-bind="attrs"
                            :input-value="selected"
                            close
                            @click="select"
                            @click:close="removeAlertStatus(item)"
                            :disabled="loading"
                            :color="item.color"
                            class="ma-1 white--text"
                          >
                            {{ $t(item.status) }}
                          </v-chip>
                        </template>

                        <!-- Listado de items con checkbox -->
                        <template v-slot:item="{ item }">
                          <v-list-item
                            ripple
                            @click="handleAlertStatusCheckboxClick(item)"
                          >
                            <v-list-item-icon class="my-3 mr-0">
                              <v-simple-checkbox
                                :value="
                                  selectedAlertStatus.some(
                                    (i) => i.id === item.id
                                  )
                                "
                                :ripple="false"
                              />
                            </v-list-item-icon>
                            <v-list-item-content>
                              <v-list-item-title>{{
                                $t(item.status)
                              }}</v-list-item-title>
                            </v-list-item-content>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </div>
                  </v-col>

                  <!-- Alerta Fecha desde -->
                  <v-col cols="12" sm="6" md="3" class="mt-2">
                    <div
                      id="alertinitialdate-wrapper"
                      style="position: relative; overflow: visible; width: auto"
                    >
                      <v-menu
                        v-model="alertInitialDateMenu"
                        ref="alertInitialDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                        :attach="'#alertinitialdate-wrapper'"
                        max-width="none"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="alertInitialDate"
                            :label="`${$t('searches.alertFrom')}`"
                            prepend-icon="mdi-calendar"
                            readonly
                            dense
                            outlined
                            clearable
                            v-bind="attrs"
                            v-on="on"
                            :rules="[]"
                            id="alert-initial-date"
                          >
                            <template v-slot:prepend>
                              <v-icon :color="iconColor(alertInitialDate)">
                                {{
                                  alertInitialDate
                                    ? 'mdi-magnify-plus'
                                    : 'mdi-calendar'
                                }}
                              </v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="alertInitialDate"
                          :locale="$t('javascriptLocale')"
                          :min="minimumDateFrom"
                          :max="maximumDateFrom"
                          no-title
                          scrollable
                          :rules="[]"
                          @input="alertInitialDateMenu = false"
                        >
                        </v-date-picker>
                      </v-menu>
                    </div>
                  </v-col>

                  <!-- Alerta Fecha hasta -->
                  <v-col cols="12" sm="6" md="3" class="mt-2">
                    <div
                      id="alertfinaldate-wrapper"
                      style="position: relative; overflow: visible; width: auto"
                    >
                      <v-menu
                        v-model="alertFinalDateMenu"
                        ref="alertFinalDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        min-width="290"
                        :attach="'#alertfinaldate-wrapper'"
                        max-width="none"
                        :content-class="isMobile ? '' : 'picker-final-menu'"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            v-model="alertFinalDate"
                            :label="`${$t('searches.alertTo')}`"
                            prepend-icon="mdi-calendar"
                            readonly
                            dense
                            outlined
                            clearable
                            v-bind="attrs"
                            v-on="on"
                            :rules="[]"
                            id="alert-final-date"
                          >
                            <template v-slot:prepend>
                              <v-icon :color="iconColor(alertFinalDate)">
                                {{
                                  alertFinalDate
                                    ? 'mdi-magnify-plus'
                                    : 'mdi-calendar'
                                }}
                              </v-icon>
                            </template>
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="alertFinalDate"
                          :locale="$t('javascriptLocale')"
                          :min="minimumDateTo"
                          no-title
                          scrollable
                          :rules="[]"
                          @input="alertFinalDateMenu = false"
                        >
                        </v-date-picker>
                      </v-menu>
                    </div>
                  </v-col>
                </v-row>

                <!-- Texto de la alerta -->
                <v-row dense class="mt-0 pt-0">
                  <v-col cols="12">
                    <v-text-field
                      v-model="alertText"
                      @focus="isAlertTextFocused = true"
                      @blur="isAlertTextFocused = false"
                      :label="alertTextLabel"
                      :disabled="loading"
                      autocomplete="false"
                      :rules="[]"
                      id="alertText"
                    >
                      <template v-slot:prepend>
                        <v-icon :color="iconColor(alertText)">
                          {{ alertText ? 'mdi-magnify-plus' : 'mdi-calendar' }}
                        </v-icon>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
              </v-sheet>
            </v-col>

            <!-- Concepto / Asunto-->
            <v-col cols="12">
              <v-text-field
                v-model="concepto"
                @focus="isConceptFocused = true"
                @blur="isConceptFocused = false"
                :label="conceptoLabel"
                prepend-icon="mdi-file-document-edit"
                :disabled="loading"
                autocomplete="false"
                :rules="[]"
                id="concepto"
              >
                <template v-slot:prepend>
                  <v-icon :color="iconColor(concepto)">
                    {{
                      concepto ? 'mdi-magnify-plus' : 'mdi-file-document-edit'
                    }}
                  </v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Observaciones -->
            <v-col cols="12">
              <v-text-field
                v-model="observation"
                @focus="isObservationFocused = true"
                @blur="isObservationFocused = false"
                :label="observationLabel"
                prepend-icon="mdi-file-document-edit"
                :disabled="loading"
                autocomplete="false"
                :rules="[]"
                id="observation"
              >
                <template v-slot:prepend>
                  <v-icon :color="iconColor(observation)">
                    {{
                      observation
                        ? 'mdi-magnify-plus'
                        : 'mdi-file-document-edit'
                    }}
                  </v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Metadatas -->
            <v-col cols="12">
              <v-text-field
                v-model="metadatas"
                @focus="isMetadatasFocused = true"
                @blur="isMetadatasFocused = false"
                :label="metadatasLabel"
                prepend-icon="mdi-database-search"
                :disabled="loading"
                autocomplete="false"
                :rules="[]"
                id="metadatas"
              >
                <template v-slot:prepend>
                  <v-icon :color="iconColor(metadatas)">
                    {{ metadatas ? 'mdi-magnify-plus' : 'mdi-database-search' }}
                  </v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Tipo de envio -->
            <v-col cols="12">
              <v-radio-group
                v-model="tipo_envio"
                :disabled="loading"
                :rules="[notEmpty]"
                prepend-icon="mdi-message-bulleted"
              >
                <template v-slot:prepend>
                  <v-icon :color="iconColor(tipo_envio !== '0')">
                    {{
                      tipo_envio !== '0'
                        ? 'mdi-magnify-plus'
                        : 'mdi-message-bulleted'
                    }}
                  </v-icon>
                </template>

                <!-- Slot que reemplaza el label -->
                <template v-slot:label>
                  <span style="font-size: 16px">
                    {{ $t('notificationType') }}
                  </span>
                </template>
                <v-row align="center" justify="center" class="my-1">
                  <v-col
                    class="justify-center align-center pb-0 pt-0 mb-0 mt-0"
                    cols="12"
                    :md="isMobile ? 12 : 4"
                    :class="{ 'mb-3': isMobile }"
                  >
                    <v-radio
                      :disabled="loading"
                      :label="$t('searches.both')"
                      value="0"
                    />
                  </v-col>

                  <v-col
                    class="justify-center align-center pb-0 pt-0 mb-0 mt-0"
                    cols="12"
                    :md="isMobile ? 12 : 4"
                    :class="{ 'mb-3': isMobile }"
                  >
                    <v-radio
                      :disabled="loading"
                      :label="$t('notification')"
                      value="2"
                    />
                  </v-col>
                  <v-col
                    class="justify-center align-center pb-0 pt-0 mb-0 mt-0"
                    cols="12"
                    :md="isMobile ? 12 : 4"
                    :class="{ 'mb-3': isMobile }"
                  >
                    <v-radio
                      :disabled="loading"
                      :label="$t('comunication')"
                      value="1"
                    />
                  </v-col>
                </v-row>
              </v-radio-group>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- Botones siempre fijos en la parte inferior -->
      <v-card-actions class="headline">
        <v-row dense justify="center">
          <v-col
            :cols="isMobile ? 6 : 'auto'"
            class="py-1"
            :class="{ 'd-flex justify-center': isMobile }"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  rounded
                  color="accept"
                  class="white--text"
                  :width="!isMobile ? '150px' : '130px'"
                  @click="undoAction"
                  id="undo-advanced-search"
                >
                  {{ $t('searches.undo') }}
                </v-btn>
              </template>
              <span>
                {{ $t('searches.undoTooltip') }}
              </span>
            </v-tooltip>
          </v-col>

          <v-col
            :cols="isMobile ? 6 : 'auto'"
            class="py-1"
            :class="{ 'd-flex justify-center': isMobile }"
          >
            <v-btn
              rounded
              color="accept"
              class="white--text"
              :width="!isMobile ? '150px' : '130px'"
              @click="resetAction"
              id="reset-advanced-search"
            >
              {{ isMobile ? $t('searches.resetShort') : $t('searches.reset') }}
            </v-btn>
          </v-col>

          <v-col
            :cols="isMobile ? 6 : 'auto'"
            class="py-1"
            :class="{ 'd-flex justify-center': isMobile }"
          >
            <v-btn
              rounded
              color="accept"
              class="white--text"
              :width="!isMobile ? '150px' : '130px'"
              :disabled="loading"
              @click="performAction"
              id="accept-advanced-search"
            >
              {{ $t('searches.accept') }}
              <template v-slot:loader>
                <v-progress-circular indeterminate size="20" width="2" />
              </template>
            </v-btn>
          </v-col>

          <v-col
            :cols="isMobile ? 6 : 'auto'"
            class="py-1"
            :class="{ 'd-flex justify-center': isMobile }"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  rounded
                  color="error"
                  class="white--text"
                  :width="!isMobile ? '150px' : '130px'"
                  @click="cancelAction"
                  id="cancel-advanced-search"
                >
                  {{ $t('searches.cancel') }}
                </v-btn>
              </template>
              <span>
                {{ $t('searches.cancelTooltip') }}
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import rules from '@/utils/rules'
import {
  listFilters /*, getPredefinedFilters*/,
} from '@/services/filter-service'
import {
  getUserOperators /*, getOperatorFilters*/,
} from '@/services/operator-service'
import { getUserInfo } from '@/services/user-service'
import { listGroups } from '@/services/groups-service'
const lodash = require('lodash')

export default {
  props: {
    cifs: Array,
    status: {
      type: Array,
      default: () => [],
    },
    users: Array,
    alertStatus: {
      type: Array,
      default: () => [],
    },
    currentAdvancedSearch: Object,
  },
  components: {},
  async beforeMount() {
    // Obtenemos todos los filtros
    this.filters = await listFilters()

    if (this.filters?.length) {
      // Getting the id of the "no filter"
      this.noFilterId = this.filters.find(
        (filter) => filter.name === 'noFilter'
      ).id
    }
  },
  async created() {
    // Inicializo desde la prop o desde el store si la prop está vacía
    this.syncCifs()

    // En el caso de que no venga el array de usuarios en la prop o venga vacio, cargamos todos los operadores,
    // solo en el caso del admin y de los operadores con permisos de admin.
    if (
      (!this.users || this.users?.length === 0) &&
      (this.isSuperAdminUser || this.isSuperOperatorUser)
    ) {
      this.usersLoaded = await getUserOperators()

      // Incluir al usuario superadmin en la lista de operadores elegibles
      const userId = this.$store.state.user.parentRef
        ? this.$store.state.user.parentRef.id
        : this.$store.state.user.id
      this.usersLoaded.push(await getUserInfo(userId))

      // y ahora tambien cargamos todos los grupos
      this.groups = await listGroups()
      this.groups.forEach((g) => {
        g.isGroup = true
      })
    } else {
      // uso la prop si tiene contenido
      //this.usersLoaded = [...this.users]
      // Para tratar los grupos, ahora divido la informacion recibida en 2 listas, usuarios y grupos.
      // entre los recibidos ahora pueden venir grupos mezclados con los usuarios, los he marcado con .isGroup
      this.usersLoaded = this.users
        .filter((u) => !u.isGroup)
        .map((u) => ({ ...u }))

      this.groups = this.users.filter((u) => u.isGroup).map((u) => ({ ...u }))
    }

    // eliminar del array los usuarios que no esten activos ??
    // se decide dejarlos porque pueden ser de notificaciones antiguas donde si que estaban activos.
    //this.usersLoaded = this.usersLoaded.filter(
    //  (item) => item.disabled === false
    //)

    // y lo mismo aplica ahora para los grupos, pueden haber sido eliminados (.deleted) pero se cargan en
    // el selector y los vamos a mostrar visualmente disabled, pero seleccionables igualmente.

    this.loadReceivedData()
    this.loading = false
  },
  async mounted() {},
  data() {
    return {
      selectedCifs: [],
      cifsNotRegisteredShowAll: false, // checkbox
      cifsNotRegistered: '',
      withoutNifTitular: true,
      ignoreNifTitular: true,
      selectedStatus: [],
      selectedFilters: [],
      customFilter: false,
      codigo_organismo_emisor: '',
      nombre_organismo_emisor: '',
      codigo_organismo_emisor_raiz: '',
      nombre_organismo_emisor_raiz: '',
      concepto: '',
      tipo_envio: '0', // ambos
      selectedUsers: [],
      selectedGroups: [],
      withoutResponsible: true,
      selectedAlertStatus: [],
      alertInitialDate: '',
      alertInitialDateMenu: false,
      alertFinalDate: '',
      alertFinalDateMenu: false,
      alertText: '',
      observation: '',
      metadatas: '',
      //
      filters: [],
      cifsLoaded: [], // copia de cifs
      usersLoaded: [], // copia de users o todos los usuarios
      groups: [], // copia de users de tipo grupo o todos los grupos
      searchCif: '',
      noFilterId: '',
      newAdvancedSearch: undefined,
      validAdvancedSearchForm: false,
      isCifsNotRegisteredFocused: false,
      isConceptFocused: false,
      isAlertTextFocused: false,
      isObservationFocused: false,
      isMetadatasFocused: false,
      searchUser: '',
      searchGroup: '',
      loading: true,
    }
  },
  computed: {
    ...mapGetters([
      'isMobile',
      'isOperatorUser',
      'isSuperAdminUser',
      'isSuperOperatorUser',
      'isEmpresaUser',
      'isAllowedRecogerNoRegistradas',
    ]),
    sortedCifs() {
      // Devuelve una copia ordenada alfabeticamente por nombre de empresa, ignorando acentos, mayus/minus, etc.
      return [...this.cifsLoaded].sort((a, b) =>
        a.empresa.localeCompare(b.empresa, undefined, { sensitivity: 'base' })
      )
    },
    cifsWithDisplay() {
      // Prepara un campo 'display' para que v-autocomplete muestre 'cif – nombre'
      return this.sortedCifs.map((item) => ({
        ...item,
        display: `${item.numeroDocIdentidad} - ${item.empresa}`,
      }))
    },
    filteredCifs() {
      // Filtrado según la cadena de búsqueda, por cif o por empresa
      const list = this.cifsWithDisplay || []
      if (!this.searchCif) return list

      const searchCap = this.searchCif.toUpperCase()
      return list.filter((item) => {
        const nro = (item.numeroDocIdentidad || '').toUpperCase()
        const emp = (item.empresa || '').toUpperCase()
        return nro.includes(searchCap) || emp.includes(searchCap)
      })
    },
    // de los filtrados quitamos los que ya están seleccionados en selectedCifs y por tanto no están como items seleccionables.
    visibleItemsCifs() {
      return this.filteredCifs.filter(
        (item) => !this.selectedCifs.includes(item.numeroDocIdentidad)
      )
    },
    cifLabel() {
      const total = this.cifsLoaded?.length || 0
      const selected = this.selectedCifs?.length || 0
      const percent = total > 0 ? Math.round((selected / total) * 100) : 0
      return selected == 0
        ? this.$t('searches.cifs')
        : this.$t('searches.cifsSelectedFromTotal', {
            selected: selected,
            total: total,
            percent: percent,
          })
    },
    // Estados ----
    iconSelectAllStatus() {
      if (this.selectedStatus?.length === this.status?.length)
        return 'mdi-close-box'
      if (this.selectedStatus?.length > 0) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    // Filtros -----
    filtersFiltered() {
      // eliminar del array el filtro 'Ver Todo' que aqui no pinta nada.
      return this.filters.filter((item) => item.id !== this.noFilterId)
    },
    iconSelectAllFilters() {
      if (this.selectedFilters?.length === this.filtersFiltered?.length)
        return 'mdi-close-box'
      if (this.selectedFilters?.length > 0) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    // Usuarios y Grupos Responsables ----
    usersFiltered() {
      // por ahora devuelve todos los usuarios cargados, recibidos en la prop o leidos a través del servicio.
      return this.usersLoaded
    },
    groupsFiltered() {
      // por ahora devuelve todos los grupos cargados, recibidos en la prop o leidos a través del servicio.
      return this.groups
    },
    sortedUsers() {
      // Devuelve una copia ordenada alfabeticamente por nombre y apellidos de usuario, ignorando acentos, mayus/minus, etc.
      return [...this.usersLoaded].sort((a, b) => {
        const nameComparison = a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        })
        return nameComparison !== 0
          ? nameComparison
          : a.surname.localeCompare(b.surname, undefined, {
              sensitivity: 'base',
            })
      })
    },
    sortedGroups() {
      // Devuelve una copia ordenada alfabeticamente por nombre de grupo, ignorando acentos, mayus/minus, etc.
      return [...this.groups].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, {
          sensitivity: 'base',
        })
      )
    },
    usersWithDisplay() {
      // Prepara un campo 'display' para que v-autocomplete muestre 'nombre apellidos'
      return this.sortedUsers?.map((item) => ({
        ...item,
        display: `${item.name || ''} ${item.surname || ''}`,
      }))
    },
    groupsWithDisplay() {
      // Prepara un campo 'display' para que v-autocomplete muestre 'nombre' en el caso de un grupo
      return this.sortedGroups.map((item) => ({
        ...item,
        display: !item.deleted
          ? item.name || ''
          : (item.name || '') + ' ' + this.$t('groupDeletedShort'),
      }))
    },
    filteredUsers() {
      // Filtrado según la cadena de búsqueda, por nombre y apellidos
      const list = this.usersWithDisplay || []
      if (!this.searchUser) return list
      const searchCap = this.searchUser.toUpperCase()
      return list.filter((item) => {
        const fullname = (item.display || '').toUpperCase()
        return fullname.includes(searchCap)
      })
    },
    filteredGroups() {
      // Filtrado según la cadena de búsqueda, por nombre
      const list = this.groupsWithDisplay || []
      if (!this.searchGroup) return list

      const searchCap = this.searchGroup.toUpperCase()
      return list.filter((item) => {
        const fullname = (item.display || '').toUpperCase()
        return fullname.includes(searchCap)
      })
    },
    visibleItemsUsers() {
      // de los filtrados quitamos los que ya están seleccionados en selectedUsers y por tanto no están como items seleccionables.
      return this.filteredUsers.filter(
        (item) => !this.selectedUsers.includes(item.id)
      )
    },
    visibleItemsGroups() {
      // de los filtrados quitamos los que ya están seleccionados en selectedGroups y por tanto no están como items seleccionables.
      return this.filteredGroups.filter(
        (item) => !this.selectedGroups.includes(item.id)
      )
    },
    userLabel() {
      const total = this.usersLoaded?.length || 0
      const selected = this.selectedUsers?.length || 0
      const percent = total > 0 ? Math.round((selected / total) * 100) : 0
      return selected == 0
        ? this.$t('searches.users')
        : this.$t('searches.usersSelectedFromTotal', {
            selected: selected,
            total: total,
            percent: percent,
          })
    },
    groupLabel() {
      const total = this.groups?.length || 0
      const selected = this.selectedGroups?.length || 0
      const percent = total > 0 ? Math.round((selected / total) * 100) : 0
      return selected == 0
        ? this.$t('searches.groups')
        : this.$t('searches.groupsSelectedFromTotal', {
            selected: selected,
            total: total,
            percent: percent,
          })
    },
    conceptoLabel() {
      return this.isConceptFocused
        ? this.$t('searches.conceptHelp')
        : this.$t('searches.concept')
    },
    // Alertas ----
    alertsWithDisplay() {
      // Prepara un campo 'display' para mostrar la traduccion
      if (!this.alertStatus || !this.alertStatus.length) {
        return []
      }
      return this.alertStatus?.map((item) => ({
        ...item,
        display: this.$t(item.status),
      }))
    },
    iconSelectAllAlertStatus() {
      if (this.selectedAlertStatus?.length === this.alertStatus?.length)
        return 'mdi-close-box'
      if (this.selectedAlertStatus?.length > 0) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    minimumDateFrom() {
      // por establecer algún límite inicial
      return new Date(Date.UTC(2020, 0, 1)).toISOString().substring(0, 10) // 2020-01-01
    },
    maximumDateFrom() {
      if (!this.alertFinalDate) {
        return null
      } else {
        return new Date(this.alertFinalDate).toISOString().substring(0, 10)
      }
    },
    minimumDateTo() {
      if (!this.alertInitialDate) {
        return null
      } else {
        // la fecha-hasta minima está limitada a la fecha-desde
        return new Date(this.alertInitialDate).toISOString().substring(0, 10)
      }
    },
    alertTextLabel() {
      return this.isAlertTextFocused
        ? this.$t('searches.alertTextHelp')
        : this.$t('searches.alertText')
    },
    observationLabel() {
      return this.isObservationFocused
        ? this.$t('searches.observationHelp')
        : this.$t('searches.observation')
    },
    metadatasLabel() {
      return this.isMetadatasFocused
        ? this.$t('searches.metadatasHelp')
        : this.$t('searches.metadatas')
    },
    cifsNotRegisteredLabel() {
      return this.isCifsNotRegisteredFocused
        ? this.$t('searches.cifsNotRegisteredHelp')
        : this.$t('searches.cifsNotRegistered')
    },
  },
  methods: {
    ...mapMutations(['']),
    ...rules,
    /**
     * Fix field for DIR3 changing it to UpperCase and removing special characters
     */
    fixAndUppercase(field) {
      if (this[field]) {
        const regex = /[^a-zA-Z0-9]/g
        this[field] = this[field].replace(regex, '').toUpperCase()
      }
    },
    loadReceivedData() {
      const current = lodash.cloneDeep(this.currentAdvancedSearch) || {}

      this.selectedCifs = current.selectedCifs ?? this.selectedCifs

      this.cifsNotRegisteredShowAll =
        current.cifsNotRegisteredShowAll ?? this.cifsNotRegisteredShowAll
      this.cifsNotRegistered =
        current.cifsNotRegistered ?? this.cifsNotRegistered

      this.withoutNifTitular =
        current.withoutNifTitular ?? this.withoutNifTitular
      this.ignoreNifTitular = current.ignoreNifTitular ?? this.ignoreNifTitular

      this.selectedStatus = current.selectedStatus ?? this.selectedStatus

      this.selectedFilters = current.selectedFilters
        ? this.getFiltersIds(current.selectedFilters)
        : this.selectedFilters

      this.customFilter = current.customFilter ?? this.customFilter
      this.codigo_organismo_emisor =
        current.codigo_organismo_emisor ?? this.codigo_organismo_emisor
      this.nombre_organismo_emisor =
        current.nombre_organismo_emisor ?? this.nombre_organismo_emisor
      this.codigo_organismo_emisor_raiz =
        current.codigo_organismo_emisor_raiz ??
        this.codigo_organismo_emisor_raiz
      this.nombre_organismo_emisor_raiz =
        current.nombre_organismo_emisor_raiz ??
        this.nombre_organismo_emisor_raiz

      this.concepto = current.concepto ?? this.concepto
      this.tipo_envio = current.tipo_envio ?? this.tipo_envio

      this.selectedUsers = current.selectedUsers
        ? this.getUsersIds(current.selectedUsers)
        : this.selectedUsers

      this.syncUsers(this.usersLoaded, current.selectedUsers)

      this.selectedGroups = current.selectedGroups
        ? this.getGroupsIds(current.selectedGroups)
        : this.selectedGroups

      this.syncGroups(this.groups, current.selectedGroups)

      this.withoutResponsible =
        current.withoutResponsible ?? this.withoutResponsible

      this.selectedAlertStatus =
        current.selectedAlertStatus ?? this.selectedAlertStatus
      this.alertInitialDate = current.alertInitialDate ?? this.alertInitialDate
      this.alertFinalDate = current.alertFinalDate ?? this.alertFinalDate
      this.alertText = current.alertText ?? this.alertText
      this.observation = current.observation ?? this.observation
      this.metadatas = current.metadatas ?? this.metadatas
    },
    getFiltersIds(selected) {
      // convierte el array de objetos filter a array de sus ids
      // y elimina aquellos que no tengan un id valido, por si acaso.
      return selected
        .map((f) => f.id)
        .filter((id) => id !== undefined && id !== null)
    },
    getUsersIds(selected) {
      // convierte el array de objetos de usuarios responsables a array de sus ids
      // y elimina aquellos que no tengan un id valido, por si acaso.
      return selected
        .map((user) => user.id)
        .filter((id) => id !== undefined && id !== null)
    },
    getGroupsIds(selected) {
      // convierte el array de objetos de grupos responsables a array de sus ids
      // y elimina aquellos que no tengan un id valido, por si acaso.
      return selected
        .map((user) => user.id)
        .filter((id) => id !== undefined && id !== null)
    },
    cancelAction() {
      this.$emit('setAdvancedSearch', null)
    },
    undoAction() {
      if (!this.currentAdvancedSearch) {
        this.resetAction()
      }
      this.loadReceivedData()
    },
    resetAction() {
      this.selectedCifs = []
      this.cifsNotRegisteredShowAll = false
      this.cifsNotRegistered = ''
      this.withoutNifTitular = true
      this.ignoreNifTitular = true
      this.selectedStatus = []
      this.selectedFilters = []
      this.customFilter = false
      this.codigo_organismo_emisor = ''
      this.nombre_organismo_emisor = ''
      this.codigo_organismo_emisor_raiz = ''
      this.nombre_organismo_emisor_raiz = ''
      this.concepto = ''
      this.tipo_envio = '0' // ambos
      this.withoutResponsible = true
      this.selectedUsers = []
      this.selectedGroups = []
      this.selectedAlertStatus = []
      this.alertInitialDate = ''
      this.alertFinalDate = ''
      this.alertText = ''
      this.observation = ''
      this.metadatas = ''
      //
      this.validAdvancedSearchForm = false
      this.searchCif = ''
    },
    async performAction() {
      if (!this.$refs.advancedSearchForm?.validate()) return
      if (
        this.alertInitialDate &&
        this.alertFinalDate &&
        this.alertInitialDate > this.alertFinalDate
      ) {
        return
      }

      // Devolver los objetos filter no solo su id
      const objFilters = this.selectedFilters.map((id) =>
        this.filtersFiltered.find((f) => f.id === id)
      )

      // Devolver los objetos completos user, no solo su id
      const objUsers = this.selectedUsers.map((id) =>
        this.usersFiltered.find((f) => f.id === id)
      )
      // Devolver los objetos completos group, no solo su id
      const objGroups = this.selectedGroups.map((id) =>
        this.groupsFiltered.find((f) => f.id === id)
      )

      // componer la respuesta a devolver, evitando devolver undefined
      let result = {
        selectedCifs: this.selectedCifs,
        cifsNotRegisteredShowAll: this.cifsNotRegisteredShowAll,
        cifsNotRegistered: this.cifsNotRegistered,
        withoutNifTitular: this.withoutNifTitular,
        ignoreNifTitular: this.ignoreNifTitular,
        selectedStatus: this.selectedStatus,
        selectedFilters: objFilters,
        customFilter: this.customFilter,
        codigo_organismo_emisor: this.customFilter
          ? this.codigo_organismo_emisor ?? ''
          : '',
        codigo_organismo_emisor_raiz: this.customFilter
          ? this.codigo_organismo_emisor_raiz ?? ''
          : '',
        nombre_organismo_emisor: this.customFilter
          ? this.nombre_organismo_emisor ?? ''
          : '',
        nombre_organismo_emisor_raiz: this.customFilter
          ? this.nombre_organismo_emisor_raiz ?? ''
          : '',
        concepto: this.concepto,
        tipo_envio: this.tipo_envio,
        selectedUsers: objUsers,
        selectedGroups: objGroups,
        withoutResponsible: this.withoutResponsible,
        selectedAlertStatus: this.selectedAlertStatus,
        alertInitialDate: this.alertInitialDate,
        alertFinalDate: this.alertFinalDate,
        alertText: this.alertText,
        observation: this.observation,
        metadatas: this.metadatas,
      }

      if (this.isEmptyObject(result)) {
        // Si el usuario pulsa 'Limpiar Todo' (reset) y luego pulsa aqui en Aceptar, es como si
        // hubiera hecho Cancelar, porque en realidad no hay nada seleccionado aparte de los valores
        // iniciales (p.e. Tipo de envio).
        result = null
      }

      this.$emit('setAdvancedSearch', result)
      this.$refs.advancedSearchForm?.reset()
      this.$refs.advancedSearchForm?.resetValidation()
    },
    // ---- Empresas ----
    syncCifs() {
      if (!this.cifs || this.cifs?.length === 0) {
        // recupero del store cuando la prop está vacia
        this.cifsLoaded = Array.isArray(this.$store.state.user.cifs)
          ? [...this.$store.state.user.cifs]
          : []
      } else {
        // uso la prop si tiene contenido
        this.cifsLoaded = [...this.cifs]
      }
    },
    customFilterCifs(item) {
      // usaremos nuestro filteredCifs como filtro personalizado
      return this.filteredCifs.some(
        (elem) => elem.numeroDocIdentidad === item.numeroDocIdentidad
      )
    },
    selectAllFilteredCifs() {
      // Selecciona de golpe todos los N resultados filtrados por la búsqueda
      const allFilteredCifs = this.visibleItemsCifs.map(
        (item) => item.numeroDocIdentidad
      )
      // Añadelos a los seleccionados previamente
      this.selectedCifs = Array.from(
        new Set([...this.selectedCifs, ...allFilteredCifs])
      )
      // Borra el contenido del input de busqueda para que no aparezca que No hay datos
      this.searchCif = ''
    },
    onItemCifsClick() {
      // Esperamos a que Vue actualice 'selectedCifs' y por tanto se actualice 'visibleItemsCifs'
      // Y entonces, si era el último visible (ya no hay más candidatos según el criterio de búsqueda), limpiamos el
      // search de busqueda para que no aparezca que No hay datos.
      this.$nextTick(() => {
        if (this.visibleItemsCifs?.length === 0) {
          this.searchCif = ''
        }
      })
    },
    // ---- Estados ----
    customStatusFilter(item, queryText) {
      return item.estado.toLowerCase().includes(queryText.toLowerCase())
    },
    selectAllStatus() {
      if (this.selectedStatus?.length === this.status?.length)
        this.selectedStatus = []
      else this.selectedStatus = this.status.map((item) => item.estado)
    },
    handleStatusCheckboxClick(status) {
      const index = this.selectedStatus.findIndex((s) => s === status.estado)
      if (index > -1) this.selectedStatus.splice(index, 1)
      else this.selectedStatus.push(status.estado)
    },
    // ---- Filtros -----
    customRoleFilter(item, queryText) {
      const name = item.isCustom
        ? item.name.toLowerCase()
        : this.$t(`filters.${item.name}`).toLowerCase()
      const searchText = queryText.toLowerCase()

      return name.includes(searchText)
    },
    selectAllFilters() {
      if (this.selectedFilters?.length === this.filtersFiltered?.length)
        this.selectedFilters = []
      else
        this.selectedFilters = this.filtersFiltered
          .map((filter) => filter.id)
          .filter((filterId) => filterId !== this.noFilterId)
    },
    handleFilterCheckboxClick(filter) {
      const index = this.selectedFilters.findIndex(
        (filterId) => filterId === filter.id
      )
      if (index > -1) this.selectedFilters.splice(index, 1)
      else this.selectedFilters.push(filter.id)
    },
    // ---- Usuarios ----
    customFilterUsers(item) {
      // usaremos nuestro filteredUsers como filtro personalizado
      return this.filteredUsers.some((elem) => elem.id === item.id)
    },
    selectAllFilteredUsers() {
      // Selecciona de golpe todos los N resultados filtrados por la búsqueda
      const allFilteredUsers = this.visibleItemsUsers.map((item) => item.id)
      // Añadelos a los seleccionados previamente
      this.selectedUsers = Array.from(
        new Set([...this.selectedUsers, ...allFilteredUsers])
      )
      // Borra el contenido del input de busqueda para que no aparezca que No hay datos
      this.searchUser = ''
    },
    onItemUsersClick() {
      // Esperamos a que Vue actualice 'selectedUsers' y por tanto se actualice 'visibleItemsUsers'
      // Y entonces, si era el último visible (ya no hay más candidatos según el criterio de búsqueda), limpiamos el
      // search de busqueda para que no aparezca que No hay datos.
      this.$nextTick(() => {
        if (this.visibleItemsUsers?.length === 0) {
          this.searchUser = ''
        }
      })
    },
    removeUser(item) {
      // Dado que en selectedUsers solo guardo los ids
      const idx = this.selectedUsers.findIndex((i) => i === item.id)
      if (idx !== -1) {
        this.selectedUsers.splice(idx, 1)
      }
    },
    syncUsers(loaded, current) {
      if (!current) return
      const loadedIds = new Set(loaded.map((item) => item.id))

      current.forEach((item) => {
        if (!loadedIds.has(item.id)) {
          loaded.push(item)
        }
      })

      return loaded
    },
    // ---- Grupos ----
    customFilterGroups(item) {
      // usaremos nuestro filteredGroups como filtro personalizado
      return this.filteredGroups.some((elem) => elem.id === item.id)
    },
    selectAllFilteredGroups() {
      // Selecciona de golpe todos los N resultados filtrados por la búsqueda
      const allFilteredGroups = this.visibleItemsGroups.map((item) => item.id)
      // Añadelos a los seleccionados previamente
      this.selectedGroups = Array.from(
        new Set([...this.selectedGroups, ...allFilteredGroups])
      )
      // Borra el contenido del input de busqueda para que no aparezca que No hay datos
      this.searchGroup = ''
    },
    onItemGroupsClick() {
      // Esperamos a que Vue actualice 'selectedGroups' y por tanto se actualice 'visibleItemsGroups'
      // Y entonces, si era el último visible (ya no hay más candidatos según el criterio de búsqueda), limpiamos el
      // search de busqueda para que no aparezca que No hay datos.
      this.$nextTick(() => {
        if (this.visibleItemsGroups?.length === 0) {
          this.searchGroup = ''
        }
      })
    },
    removeGroup(item) {
      // Dado que en selectedGroups solo guardo los ids
      const idx = this.selectedGroups.findIndex((i) => i === item.id)
      if (idx !== -1) {
        this.selectedGroups.splice(idx, 1)
      }
    },
    syncGroups(loaded, current) {
      if (!current) return
      const loadedIds = new Set(loaded.map((item) => item.id))

      current.forEach((item) => {
        if (!loadedIds.has(item.id)) {
          loaded.push(item)
        }
      })

      return loaded
    },
    // Alertas ----
    customAlertStatusFilter(item, queryText) {
      return item.status.toLowerCase().includes(queryText.toLowerCase())
    },
    selectAllAlertStatus() {
      if (this.selectedAlertStatus.length === this.alertsWithDisplay.length) {
        this.selectedAlertStatus = []
      } else {
        // clonar los objetos enteros
        this.selectedAlertStatus = [...this.alertsWithDisplay]
      }
    },
    handleAlertStatusCheckboxClick(item) {
      const index = this.selectedAlertStatus.findIndex((i) => i.id === item.id)
      if (index === -1) {
        this.selectedAlertStatus.push(item)
      } else {
        this.selectedAlertStatus.splice(index, 1)
      }
    },
    removeAlertStatus(item) {
      const idx = this.selectedAlertStatus.findIndex((i) => i.id === item.id)
      if (idx !== -1) {
        this.selectedAlertStatus.splice(idx, 1)
      }
    },
    // funciones auxiliares
    isEmptyObject(obj) {
      return Object.entries(obj).every(([key, value]) => {
        if (Array.isArray(value)) return value?.length === 0

        if (typeof value === 'string') {
          if (key === 'tipo_envio') {
            return value === '0'
          }
          return value.trim() === ''
        }

        if (typeof value === 'boolean') {
          if (key === 'cifsNotRegisteredShowAll') {
            return value === false // valor por defecto
          }
          if (key === 'withoutNifTitular' || key === 'ignoreNifTitular') {
            return value === true // valor por defecto
          }
          if (key === 'withoutResponsible') {
            return value === true // valor por defecto
          }
          return value === false // otros checkbox futuros
        }

        if (typeof value === 'number') return value === 0
        return !value // por si hay otros tipos no contemplados
      })
    },
    iconColor(value, color = 'success') {
      // Devuelve el color recibido por parametro (o el valor por defecto) si el campo tiene valor:
      // - value no es null/undefined, y
      // - si es array, su length > 0
      // - si no es array, value es truthy
      // En caso contrario devuelve undefined (usa color por defecto)
      //
      // Cuando el usuario cambia a modo noche, Vuetify invierte o ajusta esos colores internamente para que
      // sigan viéndose correctamente.
      // Si devuelve undefined, se pinta el icono con el color 'por defecto'
      if (value == null) return undefined
      if (Array.isArray(value)) {
        return value?.length > 0 ? color : undefined
      }
      return value ? color : undefined
    },
  },
  watch: {
    cifs() {
      this.syncCifs()
    },
    '$store.state.user.cifs'(newCifs) {
      // si la prop está vacía, mantengo la copia con lo del store
      if (!this.cifs || !this.cifs?.length) {
        this.cifsLoaded = Array.isArray(newCifs) ? [...newCifs] : []
      }
    },
    selectedCifs(newArr, oldArr) {
      // Cuando el usuario borra con el .X un chip (una empresa), se cierra el dropdown
      // Como solución añado este watcher. Cuando la length del array de los seleccionados disminuye, re-abrimos el dropdown.
      if (newArr?.length < oldArr?.length) {
        // Reabrimos el menú una vez que Vue haya procesado el cambio, solo si aun hay elementos.
        // Despues de hacer click en el boton 'Limpiar Todo' no quiero darle el foco ni abrirlo.
        this.$nextTick(() => {
          if (newArr?.length > 0) {
            this.$refs.cifsAutocomplete.focus()
            this.$refs.cifsAutocomplete.isMenuActive = true
          }
        })
      }
    },
    customFilter: function (newValue) {
      // Checkbox de filtro personalizado, al seleccionarlo se limpian los filtros que estuviesen seleccionados
      if (newValue === true) this.selectedFilters = []
      if (newValue === false) {
        this.codigo_organismo_emisor = ''
        this.codigo_organismo_emisor_raiz = ''
        this.nombre_organismo_emisor = ''
        this.nombre_organismo_emisor_raiz = ''
      }
    },
    selectedUsers(newArr, oldArr) {
      // Cuando el usuario borra con el .X un chip (un usuario responsable), se cierra el dropdown
      // Como solución añado este watcher. Cuando la length del array de los seleccionados disminuye, re-abrimos el dropdown.
      if (newArr?.length < oldArr?.length) {
        // Reabrimos el menú una vez que Vue haya procesado el cambio, solo si aun hay elementos.
        // Despues de hacer click en el boton 'Limpiar Todo' no quiero darle el foco ni abrirlo.
        this.$nextTick(() => {
          if (newArr?.length > 0) {
            this.$refs.usersAutocomplete.focus()
            this.$refs.usersAutocomplete.isMenuActive = true
          }
        })
      }
    },
    selectedGroups(newArr, oldArr) {
      // Cuando el usuario borra con el .X un chip (un grupo responsable), se cierra el dropdown
      // Como solución añado este watcher. Cuando la length del array de los seleccionados disminuye, re-abrimos el dropdown.
      if (newArr?.length < oldArr?.length) {
        // Reabrimos el menú una vez que Vue haya procesado el cambio, solo si aun hay elementos.
        // Despues de hacer click en el boton 'Limpiar Todo' no quiero darle el foco ni abrirlo.
        this.$nextTick(() => {
          if (newArr?.length > 0) {
            this.$refs.groupsAutocomplete.focus()
            this.$refs.groupsAutocomplete.isMenuActive = true
          }
        })
      }
    },
    cifsNotRegisteredShowAll(newVal) {
      // Al activar el checkbox, limpiar el texto libre de cifs o nombres separados por coma.
      if (newVal === true && this.cifsNotRegistered !== '') {
        this.cifsNotRegistered = ''
      }
    },
    cifsNotRegistered(newVal) {
      // y viceversa, al escribir algo en el texto libre de cifs o nombres, desactivar el checkbox
      if (newVal && this.cifsNotRegisteredShowAll === true) {
        this.cifsNotRegisteredShowAll = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
/* hacer más grande el tamaño del icono que abre y cierra los desplegables v-autocomplete y v-select */
/* y ponerle un cursor al v-autocomplete de empresas porque no aparece ninguno */
::v-deep .v-input__append-inner .v-icon.mdi-menu-down {
  font-size: 40px !important;
  cursor: pointer !important;
  line-height: 1;
}

/* contenedor de chips seleccionados dentro de los v-autocomplete */
::v-deep .v-select.v-select--chips .v-select__selections {
  max-height: 100px !important; /* altura máxima antes de aparecer scroll */
  overflow-y: auto !important; /* scroll vertical */
}

.custom-group-header {
  position: relative;
  display: inline-block; /* ajusta ancho al contenido */
  top: -32px; /* sube sobre el borde */
  background-color: inherit;
  padding: 0 8px;
  margin-left: -8px;
}

::v-deep .picker-final-menu {
  /* hack: este desplazamiento compensa el cálculo de posición por defecto.*/
  /* desplaza todo el menú 90px a la izquierda */
  transform: translateX(-90px) !important;
}

/* Para los selectores de estados: */
/* 1. El slot que envuelve los inputs/chips */
.custom-multiline-select .v-input__slot {
  height: auto !important;
  min-height: auto !important;
  flex-wrap: wrap !important;
  align-items: flex-start !important;
}

/* 2. El wrapper específico de los selects/chips */
.custom-multiline-select .v-select__selections {
  height: auto !important;
  min-height: auto !important;
  flex-wrap: wrap !important;
  align-items: flex-start !important;
}

/* 3. Ajuste opcional de margen vertical para que no queden tan separados */
.custom-multiline-select .v-chip {
  margin-top: 2px !important;
  margin-bottom: 2px !important;
}

/* Chip personalizado para el caso de Grupos que estan deleted, 
 * poder mostrarlo visualmente como disabled, aunque responde a los eventos, 
 * y sigue funcionando el click en el aspa de eliminacion.
 */
.custom-chip-disabled {
  opacity: 0.6;
  pointer-events: auto; /* permite borrar */
}

/* Chip personalizado, mas grande (alto) en el caso de Grupos, 
 * para poder meter luego dentro un avatar mas grande.
 */
.custom-chip-tall {
  height: 40px;
  min-height: 40px;
  padding-left: 8px;
  padding-right: 6px;
  display: flex;
  align-items: center;
}

/* Avatar de grupo dentro del v-chip, seleccion de Grupos en las busquedas avanzadas.
 * Con asignar la propiedad :avatarSize= no ha funcionado, asi que se sobreescriben las props 
 * del componente v-avatar de vuetify para el caso del v-chip personalizado, custom-chip-tall
*/
::v-deep(.v-chip.custom-chip-tall .v-avatar) {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
}
</style>
